import { readStorageState } from '@luminovo/frontend-utils';
import { type Atom, createAtom } from '@tanstack/react-store';
import React from 'react';

/**
 * An atom for one table state slice, created once from storage, that writes each change back while `enabled`.
 *
 * The subscription is a layout effect, so it is in place before any `useEffect` runs, a child's included. Only a
 * write from a child's layout effect, made before it, is neither saved nor passed to `onChange`.
 */
export function usePersistedAtom<T>(
    key: string,
    fallback: T,
    {
        storage,
        enabled,
        isValid,
        onChange,
    }: { storage: Storage; enabled: boolean; isValid: (value: unknown) => boolean; onChange?: (value: T) => void },
): Atom<T> {
    const [atom] = React.useState(() =>
        createAtom<T>(readStorageState(key, fallback, { storage, enabled, validation: isValid })),
    );

    React.useLayoutEffect(() => {
        const { unsubscribe } = atom.subscribe((value) => {
            if (enabled) {
                // Not `setStorageState`: its serializability check rejects v9's prototype-less maps.
                storage.setItem(key, JSON.stringify(value));
            }
            onChange?.(value);
        });
        return unsubscribe;
    }, [atom, key, storage, enabled, onChange]);

    return atom;
}
